import React from 'react';
import { motion } from 'framer-motion';
import corridorBg from '../assets/scifi_corridor.jpg';

export default function TechBackground() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none dark:block">
      {/* 
        1. Sci-Fi Cybernetic Corridor Tunnel (Hidden/Subtle in light mode, active in dark mode)
      */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700 opacity-50 dark:opacity-50 light:opacity-0"
        style={{
          backgroundImage: `url(${corridorBg})`,
        }}
      />

      {/* 
        2. Smooth Deep Navy Darkening Overlay
      */}
      <div className="absolute inset-0 bg-[#1b2d48]/75 backdrop-blur-[2px] light:bg-white/90" />

      {/* 
        3. Subtle laser energy pulses
      */}
      <svg
        className="absolute inset-0 w-full h-full opacity-40 light:opacity-0"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        viewBox="0 0 1920 1080"
      >
        <defs>
          <filter id="wire-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <linearGradient id="stream-cyan" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00f0ff" stopOpacity="0" />
            <stop offset="50%" stopColor="#00f0ff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="stream-blue" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
            <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Perspective Wire Guides matching tunnel depth */}
        {/* Left Wall Corridor Laser Wire */}
        <path
          d="M 120,180 L 450,340 L 720,440 L 860,500 L 960,540 L 860,580 L 720,640 L 450,740 L 120,900"
          fill="none"
          stroke="rgba(0, 240, 255, 0.15)"
          strokeWidth="1.2"
        />

        {/* Right Wall Corridor Laser Wire */}
        <path
          d="M 1800,180 L 1470,340 L 1200,440 L 1060,500 L 960,540 L 1060,580 L 1200,640 L 1470,740 L 1800,900"
          fill="none"
          stroke="rgba(0, 240, 255, 0.15)"
          strokeWidth="1.2"
        />

        {/* Floor Center Energy Runway Wires */}
        <path
          d="M 450,1080 L 780,720 L 960,540 L 1140,720 L 1470,1080"
          fill="none"
          stroke="rgba(56, 189, 248, 0.2)"
          strokeWidth="1.5"
        />

        {/* ========================================================== */}
        {/* DATA ENERGY BEAMS FLOWING BACK & FORTH (Real Circuit Flow)  */}
        {/* ========================================================== */}

        {/* Beam 1: Left Wall - Traveling Forward deep into the tunnel */}
        <circle r="4" fill="#ffffff" filter="url(#wire-glow)">
          <animateMotion
            path="M 120,180 L 450,340 L 720,440 L 860,500 L 960,540"
            dur="4.5s"
            repeatCount="indefinite"
          />
        </circle>
        <circle r="7" fill="#00f0ff" opacity="0.8" filter="url(#wire-glow)">
          <animateMotion
            path="M 120,180 L 450,340 L 720,440 L 860,500 L 960,540"
            dur="4.5s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Beam 2: Left Wall Lower - Traveling Outward from deep tunnel to foreground */}
        <circle r="3.5" fill="#ffffff" filter="url(#wire-glow)">
          <animateMotion
            path="M 960,540 L 860,580 L 720,640 L 450,740 L 120,900"
            dur="5.5s"
            begin="1.8s"
            repeatCount="indefinite"
          />
        </circle>
        <circle r="6" fill="#38bdf8" opacity="0.75" filter="url(#wire-glow)">
          <animateMotion
            path="M 960,540 L 860,580 L 720,640 L 450,740 L 120,900"
            dur="5.5s"
            begin="1.8s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Beam 3: Right Wall - Traveling Inward to core */}
        <circle r="4" fill="#ffffff" filter="url(#wire-glow)">
          <animateMotion
            path="M 1800,180 L 1470,340 L 1200,440 L 1060,500 L 960,540"
            dur="4.8s"
            begin="0.8s"
            repeatCount="indefinite"
          />
        </circle>
        <circle r="7" fill="#00f0ff" opacity="0.8" filter="url(#wire-glow)">
          <animateMotion
            path="M 1800,180 L 1470,340 L 1200,440 L 1060,500 L 960,540"
            dur="4.8s"
            begin="0.8s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Beam 4: Floor Runway - Traveling Rapidly into Depth */}
        <circle r="4.5" fill="#ffffff" filter="url(#wire-glow)">
          <animateMotion
            path="M 450,1080 L 780,720 L 960,540"
            dur="3.8s"
            begin="0.4s"
            repeatCount="indefinite"
          />
        </circle>
        <circle r="8" fill="#60a5fa" opacity="0.85" filter="url(#wire-glow)">
          <animateMotion
            path="M 450,1080 L 780,720 L 960,540"
            dur="3.8s"
            begin="0.4s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Beam 5: Floor Right Runway - Outward flow */}
        <circle r="4" fill="#ffffff" filter="url(#wire-glow)">
          <animateMotion
            path="M 960,540 L 1140,720 L 1470,1080"
            dur="4.2s"
            begin="2.2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle r="7" fill="#00f0ff" opacity="0.8" filter="url(#wire-glow)">
          <animateMotion
            path="M 960,540 L 1140,720 L 1470,1080"
            dur="4.2s"
            begin="2.2s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Subtle Depth Perspective Core Horizon Glow (Constant, steady, no blink) */}
        <circle
          cx="960"
          cy="540"
          r="45"
          fill="#00f0ff"
          opacity="0.25"
          filter="url(#wire-glow)"
        />
      </svg>
    </div>
  );
}
